import { z } from 'zod';
import type { TagTone } from '../components/ui/StatusTag';

export const partnerSettingTier = z.enum(['operational', 'economic', 'ceiling']);
export type PartnerSettingTier = z.infer<typeof partnerSettingTier>;

export const partnerSetting = z.object({
  key: z.string(),
  label: z.string(),
  tier: partnerSettingTier,
  kind: z.enum(['money', 'integer', 'boolean']),
  help: z.string(),
  raiseIsRiskier: z.boolean(),
  value: z.unknown(),
  /** What the field shows and accepts back. Money is an ordinary decimal. */
  display: z.string(),
});
export type PartnerSetting = z.infer<typeof partnerSetting>;

export const partnerAuditEntry = z.object({
  at: z.string(),
  actor: z.string(),
  type: z.string(),
  detail: z.string(),
});

export const partnerSettings = z.object({
  settings: z.array(partnerSetting),
  configVersion: z.number(),
  feedUrl: z.string(),
  recent: z.array(partnerAuditEntry).nullable(),
});
export type PartnerSettings = z.infer<typeof partnerSettings>;

/** Most dangerous last, so the page reads as an escalation. */
export const tierOrder: PartnerSettingTier[] = ['operational', 'economic', 'ceiling'];

export const tierTone: Record<PartnerSettingTier, TagTone> = {
  operational: 'neutral',
  economic: 'warn',
  ceiling: 'danger',
};

export const tierCopy: Record<PartnerSettingTier, { title: string; blurb: string }> = {
  operational: {
    title: 'Operational',
    blurb:
      'Timing and polling. Safe to tune, and expected to be tuned once there is real latency data from the daily close.',
  },
  economic: {
    title: 'Economics',
    blurb:
      'How candidates are judged. Anything already discovered keeps the values it was found under, so changing these splits the population into groups that cannot be totalled together. The config version is bumped so they stay separable.',
  },
  ceiling: {
    title: 'Ceilings',
    blurb:
      'The limits on what can be spent. Lowering one is always safe. Raising one widens the exposure and has to be confirmed by hand.',
  },
};
